import React, {memo, FC, useState, useEffect} from "react";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import {Button} from "antd";
import {lime, magenta, orange, purple} from "@ant-design/colors";
import Page from "../../../views/Page/Page";
import Section from "../../../views/Section/Section";
import CardGrid from "./CardGrid";
import CardBookings, {cardBookings} from "../Cards/CardBookings";
import CardUpcomingCourses, {
    cardUpcomingCourses,
} from "../Cards/CardUpcomingCourses";
import CardCoursesWithWaitlist, {
    cardCoursesWithWaitlist,
} from "../Cards/CardCoursesWithWaitlist";
import CardBookingsStartedCourses from "../Cards/CardBookingsStartedCourses";
// Phase 16 Plan 16-07 (REPORT-03): Mollie-KPI-Dashboard-Widget — featureMollie-gated
import CardMollieKpi from "../Cards/CardMollieKpi";
// Online-Registrierungen-Kachel — nur Admin/Superuser (Datenschutz: Kontaktdaten)
import CardOnlineBookers from "../Cards/CardOnlineBookers";
import useHighestRole from "../../../helpers/useHighestRole";
import Roles from "../../../../shared/models/roles";
import {atLeast} from "../../common/AuthenticateSetup";
import i18n from "../../../services/i18n";

export type DashboardConfig = {
    dashColumns: number;
    cardHeight: number;
    innerCardHeight?: number;
};
const dashboardConfig: DashboardConfig = {
    dashColumns: 2,
    cardHeight: 510,
    innerCardHeight: 460,
};

export type DraggableCard = {
    id: string;
    sortIndex: number;
    title: string | React.ReactNode;
    content: string | React.ReactNode;
    actions?: Array<React.ReactNode>;
    extra?: React.ReactNode;
};

export type DesignColor = Array<string> & {
    primary?: string | undefined;
};

type DashboardOverviewProps = {};

const cardStyle: React.CSSProperties = {
    // maxWidth: "100%",
};

const DashboardOverview: FC<DashboardOverviewProps> = () => {
    const [cards, setCards] = useState<Array<DraggableCard>>([
        {
            id: "1",
            sortIndex: 1,
            title: cardBookings.title,
            content: (
                <CardBookings
                    color={orange}
                    dashboardConfig={dashboardConfig}
                    style={cardStyle}
                />
            ),
            actions: [<Button key="deleteBtn">{"entfernen"}</Button>],
            extra: cardBookings.extra,
        },
        {
            id: "2",
            sortIndex: 2,
            title: cardUpcomingCourses.title,
            content: (
                <CardUpcomingCourses
                    color={lime}
                    dashboardConfig={dashboardConfig}
                    style={cardStyle}
                />
            ),
        },
        {
            id: "3",
            sortIndex: 3,
            title: cardCoursesWithWaitlist.title,
            content: (
                <CardCoursesWithWaitlist
                    color={magenta}
                    dashboardConfig={dashboardConfig}
                    style={cardStyle}
                />
            ),
            actions: [<Button key="deleteBtn">{"entfernen"}</Button>],
            extra: cardCoursesWithWaitlist.extra,
        },
        {
            id: "4",
            sortIndex: 4,
            title: "hi 4",
            content: (
                <CardBookingsStartedCourses
                    color={purple}
                    dashboardConfig={dashboardConfig}
                    style={cardStyle}
                />
            ),
        },
        // {
        //     id: "4",
        //     sortIndex: 4,
        //     title: "hi 4",
        //     content: (
        //         <CardTest color={purple} dashboardConfig={dashboardConfig} />
        //     ),
        // },
        // {
        //     id: "5",
        //     sortIndex: 5,
        //     title: "hi 5",
        //     content: (
        //         <CardTest color={green} dashboardConfig={dashboardConfig} />
        //     ),
        // },
        // Phase 16 Plan 16-07 (REPORT-03): Mollie-KPI-Widget.
        // CardMollieKpi rendert null bei featureMollie=false (D-15) — kein Leerraum.
        {
            id: "mollie-kpi",
            sortIndex: 99,
            title: "Mollie-Zahlungen",
            content: <CardMollieKpi style={cardStyle} />,
            actions: [],
            extra: null,
        },
    ]);

    // Datenschutz: Online-Registrierungen-Kachel nur fuer Admin und hoeher.
    // Hierarchie-Check via atLeast(Admin) -> Admin, AreaManager, SuperAdmin,
    // Superuser. Bewusst hierarchisch (nicht exakt ===), damit die Sichtbarkeit
    // mit dem Server-Gating @Authorized([Admin, Superuser]) in
    // booker.resolver.ts uebereinstimmt (customAuthChecker.roleImplies laesst
    // hoehere Rollen die Admin-Anforderung erfuellen). useHighestRole liefert
    // initial einen Default (async-Flash) -> die Karte wird NICHT im
    // useState-Initializer eingefuegt, sondern reaktiv per useEffect bei
    // Aenderung von canSee hinzugefuegt/entfernt.
    const highestRole = useHighestRole();
    const canSeeOnlineBookers = atLeast(Roles.Admin).includes(highestRole);

    useEffect(() => {
        const onlineBookersCard: DraggableCard = {
            id: "online-bookers",
            sortIndex: 5,
            title: i18n.containers.dashboard.CardOnlineBookers.cardTitle(),
            content: (
                <CardOnlineBookers
                    dashboardConfig={dashboardConfig}
                    style={cardStyle}
                />
            ),
            actions: [],
            extra: null,
        };

        setCards((prev) => {
            const has = prev.some((card) => card.id === "online-bookers");
            if (canSeeOnlineBookers && !has) {
                return [...prev, onlineBookersCard];
            }
            if (!canSeeOnlineBookers && has) {
                return prev.filter((card) => card.id !== "online-bookers");
            }

            return prev;
        });
    }, [canSeeOnlineBookers]);

    return (
        <DndProvider backend={HTML5Backend}>
            <Page
                data-testid="DashboardOverview"
                content={
                    <Section
                        showHeader={false}
                        content={
                            <CardGrid
                                cards={cards}
                                setCards={setCards}
                                dashboardConfig={dashboardConfig}
                            />
                        }
                    />
                }
            />
        </DndProvider>
    );
};

export default memo(DashboardOverview);
